import { LoaderBar } from '@components/LoaderBar';
import { AuthProvider } from '@contexts/auth';
import { IntegrationProvider } from '@contexts/integrations';
import { NotificationProvider } from '@contexts/notification';
import { LegacyRouteGuard } from '@features/page-versions';
import { OrgMetadataProvider } from '@contexts/org-metadata';
import { SidebarProvider } from '@contexts/sidebar';
import { useClientConfig } from '@hooks/client-config';
import { metadataHook } from '@hooks/metadata';
import Sidebar from '@layouts/Sidebar';
import SecureHeader from '@layouts/SecureHeader/index';
import { ContentCard } from '@layouts/ContentCard';
import { SecureHeaderProvider } from '@contexts/secure-header';
import { SkupremeMetadata } from '@layouts/components/Metadata';
import ScriptsHeader from '@layouts/components/ScriptsHeader';
import { BrowserUtility } from '@utility/browser-utility';
import { CommonConstant } from '@utility/constants/common';
import { GAService } from '@utility/ga';
import FirebaseService from '@utility/services/firebase';
import { useEffect } from 'react';
import { Outlet, useHref, useNavigate } from 'react-router';
import { RouterProvider } from '@skupreme/shared/ui';
import { brand } from '@utility/config';
import { AccountLevelNotice } from '@features/account-view/AccountLevelNotice';

import './styles.css';
import { useHealth } from '@hooks/health';
import { ApiNotActive } from '@page-components/error/ApiNotActive';
import { InternetCheck } from '@page-components/error/InternetCheck';
import ErrorBoundary from '@components/ErrorBoundry';

function App() {
  useClientConfig();

  /* Sent to `RouterProvider` so React Aria links go through the router. */
  const navigate = useNavigate();

  const { metadata } = metadataHook();
  const { isApiStatusOK } = useHealth();

  useEffect(() => {
    BrowserUtility.remove(CommonConstant.tutorialVideos);
    GAService.init();
    FirebaseService.initApp();
  }, []);

  /* @ts-expect-error - TS2339: Property 'metadata' does not exist on type 'never'. */
  const isLoading = Object.keys(metadata).length === 0;

  function FallBackUI() {
    if (isLoading) {
      return <LoaderBar />;
    }
    if (!isApiStatusOK) {
      return <ApiNotActive />;
    }
    return <></>;
  }

  return (
    <ErrorBoundary>
      <RouterProvider navigate={navigate} useHref={useHref}>
        <SkupremeMetadata />
        <ScriptsHeader />
        {isLoading || !isApiStatusOK ? (
          <FallBackUI />
        ) : (
          <AuthProvider>
            <NotificationProvider>
              <OrgMetadataProvider>
                <SidebarProvider>
                  <IntegrationProvider>
                    <SecureHeaderProvider>
                      <>
                        {/* Fixed to the top of the window, over whatever
                                the layout puts there. */}
                        <InternetCheck />
                        <div className="sk:flex sk:min-h-0 sk:flex-auto sk:bg-shell">
                          <Sidebar />
                          <ContentCard>
                            <SecureHeader />
                            <AccountLevelNotice />
                            {/* A V2 account never opens a legacy-only page. */}
                            <LegacyRouteGuard />
                            <Outlet />
                          </ContentCard>
                        </div>
                      </>
                    </SecureHeaderProvider>
                  </IntegrationProvider>
                </SidebarProvider>
              </OrgMetadataProvider>
            </NotificationProvider>
          </AuthProvider>
        )}
      </RouterProvider>
    </ErrorBoundary>
  );
}

export default App;
